/**
 * Centralized WhatsApp URL and messaging helper
 * Business: Al Ishrat Document Clearing Service (+971 58 615 5899)
 */

export const WHATSAPP_PHONE = '+971 58 615 5899'
export const WHATSAPP_NUMBER_CLEAN = '971586155899'
export const PHONE_CALL_URL = 'tel:+971586155899'

export type WhatsAppInquiryType =
  | 'general'
  | 'service'
  | 'consultation'
  | 'urgent'
  | 'directions'
  | 'quote'
  | 'formFallback'
  | 'custom'

export interface WhatsAppUrlOptions {
  type?: WhatsAppInquiryType
  service?: string
  name?: string
  phone?: string
  price?: string
  customMessage?: string
}

/**
 * Generate properly encoded WhatsApp chat link with pre-filled inquiry text.
 * Prevents blank chat drop-off and increases Google Ads conversion rate.
 */
export function getWhatsAppUrl(options: WhatsAppUrlOptions = {}): string {
  const { type = 'general', service, name, phone, price, customMessage } = options

  let message = ''

  switch (type) {
    case 'service':
      if (service && price) {
        message = `Hi, I am interested in ${service} (Starting from ${price}) in Dubai / UAE. Please share requirements and quotation.`
      } else if (service) {
        message = `Hi, I am interested in ${service} in Dubai / UAE. Please share the requirements, pricing, and process.`
      } else {
        message = `Hi, I would like to inquire about your document clearing services in Dubai / UAE. Please share pricing and process.`
      }
      break

    case 'quote':
      message = service
        ? `Hi, I would like to request a quick quote for ${service} in Dubai / UAE.`
        : `Hi, I would like to request a quick quote for document clearing in Dubai / UAE.`
      break

    case 'consultation':
      message = service
        ? `Hello, I would like to request a free consultation regarding ${service}.`
        : `Hello, I would like to request a free consultation regarding document clearing & government services in Dubai.`
      break

    case 'urgent':
      message = `Hi, I need urgent same-day assistance with document clearing / attestation in Dubai. Please contact me as soon as possible.`
      break

    case 'directions':
      message = `Hi, I would like directions and office hours for your Al Karama, Dubai office.`
      break

    case 'formFallback': {
      const parts: string[] = ['Hi, I would like to inquire regarding your services.']
      if (name) parts.push(`Name: ${name}`)
      if (phone) parts.push(`Phone: ${phone}`)
      if (service) parts.push(`Service Required: ${service}`)
      if (customMessage) parts.push(`Details: ${customMessage}`)
      parts.push('Please get in touch with me.')
      message = parts.join('\n')
      break
    }

    case 'custom':
      message = customMessage || `Hi, I would like to inquire about your document clearing services in Dubai.`
      break

    case 'general':
    default:
      message = service
        ? `Hi, I would like to inquire about ${service} in Dubai / UAE. Please share pricing and process.`
        : `Hi, I would like to inquire about document clearing services in Dubai / UAE. Please share pricing and process.`
      break
  }

  return `https://wa.me/${WHATSAPP_NUMBER_CLEAN}?text=${encodeURIComponent(message)}`
}
